import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import prUrls from 'src/redux/pull-request/urls';
import { getBranchViewDestinationRepoName } from 'src/redux/repository/branch/branch-redux';
import {
  getCurrentRepositoryFullSlug,
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
} from 'src/selectors/repository-selectors';
import escapeBbqlString from 'src/utils/escape-bbql-string';
import { ContextLineRanges } from 'src/utils/validate-context-lines';

import urls from '../../urls';

const baseInternalUrl = '/!api/internal/repositories';

// Generated URLs to be used in links/buttons - these include the actual workspace/repo name.
export const useBranchUrls = () => {
  const repoOwner = useSelector(getCurrentRepositoryOwnerName);
  const repoSlug = useSelector(getCurrentRepositorySlug) || '';
  const repoFullSlug = useSelector(getCurrentRepositoryFullSlug) || '';
  const destRepoName = useSelector(getBranchViewDestinationRepoName) || '';

  return {
    branchesPageUrl: urls.ui.branches(repoOwner, repoSlug),
    specBranchSyncInfoUrl: useCallback(
      (sourceHash: string, destHash: string) => {
        return `/!api/internal/repositories/${repoOwner}/${repoSlug}/branch-sync-info-spec/${repoOwner}/${repoSlug}:${sourceHash}%0D${destRepoName}:${destHash}`;
      },
      [repoOwner, repoSlug, destRepoName]
    ),
    sourceApiUrl: useCallback(
      (
        sourceHash: string,
        filepath: string,
        contextLineRange: ContextLineRanges
      ) => {
        return prUrls.api.internal.contextFromSrcTopicDiff(
          {
            owner: repoOwner,
            slug: repoSlug,
            sourceHash,
            filepath,
          },
          contextLineRange
        );
      },
      [repoOwner, repoSlug]
    ),
    branchPullRequests: useCallback(
      (sourceRef: string, destRef: string) => {
        // BBQL query for branch pull requests
        const bbqlQuery = `source.branch.name="${escapeBbqlString(
          sourceRef
        )}" AND source.repository.full_name="${escapeBbqlString(
          repoFullSlug
        )}" AND destination.repository.full_name="${escapeBbqlString(
          destRepoName
        )}" AND destination.branch.name="${escapeBbqlString(destRef)}"`;

        const bbql = encodeURIComponent(bbqlQuery);
        const required_fields =
          'next,values.links,values.title,values.id,values.state,values.created_on,values.closed_on';

        return `/!api/2.0/repositories/${repoFullSlug}/pullrequests/?fields=${required_fields}&q=${bbql}`;
      },
      [repoFullSlug, destRepoName]
    ),
    branchBuildStatuses: useCallback(
      (branch: string) => {
        return `${baseInternalUrl}/${repoOwner}/${repoSlug}/branch/statuses/${encodeURIComponent(
          branch
        )}?pagelen=100`;
      },
      [repoOwner, repoSlug]
    ),
  };
};
